import React, { useEffect, useRef, useState } from 'react'
import { Form, FormikProvider, useFormik } from 'formik'
import { Edit, Search } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { all, isAxiosError } from 'axios'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'

import { useCompanyMutation, useCompanyQuery } from 'shared/store/useCompanyDetails'
import { findCountryKeyByName } from 'shared/utils/helpers/findCountryName'
import { CompanySettingsType } from 'shared/store/useCompanyDetails/types'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import getCompanyData from 'shared/services/getCompanyData'
import useCountriesQuery from 'shared/store/useCountries'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'
import useLogin from 'shared/store/useLogin'
import { UserRoles } from 'shared/consts'
import { httpClient } from 'shared/lib'

import { TextField } from '@mui/material'
import UploadLogoAdmin from '../UploadLogoAdmin'
import { companySchema } from '../../schema'

export default function CompanyForm() {
   const { t } = useTranslation(['TRANSLATION', 'COMPANY_SETTINGS'])
   const { data } = useCountriesQuery()
   const { user } = useLogin()
   const { data: companyData } = useCompanyQuery()
   const [isEditingName, setIsEditingName] = useState(false)
   const queryClient = useQueryClient()
   const { mutate, isLoading } = useCompanyMutation()
   const textFieldRef = useRef<HTMLInputElement>(null)
   const [options, setOptions] = useState<{ voivodeshipOptions: OptionType[]; countyOptions: OptionType[] }>({
      voivodeshipOptions: [],
      countyOptions: [],
   })
   const [isCompanyDataFetching, setIsCompanyDataFetching] = useState(false)

   const formik = useFormik<CompanySettingsType>({
      initialValues: {
         blocked: false,
         blockReason: null,
         companyName: '',
         taxID: '',
         typeService: {
            workshopPositions: 0,
            shopPositions: 0,
            shop: false,
            workshop: false,
         },
         address: {
            street: '',
            zipCode: '',
            city: '',
            country: 'Poland',
            houseNumber: '',
            county: '',
            voivodeship: '',
         },
      },
      validationSchema: companySchema,
      onSubmit: handleSubmit,
   })

   const { values, setTouched, setValues, setFieldValue, validateForm, setErrors, setFieldError } = formik

   async function handleOnSearch() {
      setIsCompanyDataFetching(true)
      await getCompanyData(values.taxID!)
         .then((res) => {
            if (res) {
               setValues(
                  {
                     ...values,
                     companyName: res.companyName,
                     address: {
                        country: 'Poland',
                        voivodeship: res.voivodeship ?? values.address.voivodeship,
                        houseNumber: res.houseNumber ?? values.address.houseNumber,
                        county: res.county ?? values.address.county,
                        city: res.city ?? values.address.city,
                        street: res.street ?? values.address.street,
                        zipCode: res.postalCode ?? values.address.zipCode,
                     },
                  },
                  true
               )
            }
            validateForm({
               ...values,
               companyName: res.companyName,
               address: {
                  country: 'Poland',
                  voivodeship: res.voivodeship,
                  houseNumber: res.houseNumber,
                  county: res.county,
                  city: res.city,
                  street: res.street,
                  zipCode: res.postalCode,
               },
            }).then((errors) => {
               const allFieldsTouched = touchAllFields(companyData)
               setTouched(allFieldsTouched).then(() => {
                  setErrors(errors)
               })
            })
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               setFieldError('taxID', t(`TRANSLATION:${error.response?.data?.errors[0].code}`))
            } else {
               setFieldError('taxID', error)
            }
         })
         .finally(() => setIsCompanyDataFetching(false))
   }

   function handleSubmit() {
      if (data && companyData) {
         if (user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) && values) {
            const newCompanyData: CompanySettingsType = {
               ...values,
               typeService: {
                  ...values.typeService,
                  shop: values.typeService.shopPositions > 0,
                  workshop: values.typeService.workshopPositions > 0,
               },
               address: {
                  ...values.address,
                  country: findCountryKeyByName(data, values.address?.country || 'Poland') || 'Poland',
               },
            }
            mutate(newCompanyData, {
               onSuccess: () => {
                  queryClient.invalidateQueries('userSettings')
               },
            })
         }
      }
   }

   useEffect(() => {
      if (
         options.countyOptions.length > 0 &&
         !options.countyOptions.some((item) => item.name === values?.address.county)
      ) {
         setFieldValue('address.county', '')
      }
   }, [options])

   useEffect(() => {
      if (companyData && companyData && data) {
         setValues({
            ...companyData,
         })
         if (!companyData?.companyName) {
            setIsEditingName(true)
         }
         validateForm(companyData).then((errors) => {
            const allFieldsTouched = touchAllFields(companyData)
            setTouched(allFieldsTouched).then(() => {
               setErrors(errors)
            })
         })
      }
   }, [companyData, data, httpClient])

   useEffect(() => {
      if (data && values && companyData) {
         const selectedCountry = data[companyData.address?.country || 'Poland']
         if (selectedCountry && selectedCountry.name === 'Polska') {
            if (selectedCountry.voivodeships) {
               setOptions({
                  voivodeshipOptions: Object.keys(selectedCountry.voivodeships).map((item) => ({
                     name: item,
                     value: item,
                  })),
                  countyOptions: values.address.voivodeship
                     ? selectedCountry.voivodeships[values.address.voivodeship].map((item) => ({
                          name: item,
                          value: item,
                       }))
                     : [],
               })
            }
         }
      }
   }, [data, values, companyData])

   function touchAllFields(obj: any): any {
      if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
         return Object.keys(obj).reduce(
            (acc, key) => {
               acc[key] = touchAllFields(obj[key])
               return acc
            },
            {} as { [key: string]: boolean | typeof obj }
         )
      }

      return true
   }

   useEffect(() => {
      if (isEditingName && textFieldRef.current) {
         textFieldRef.current.focus()
      }
   }, [isEditingName])

   return (
      <Grid container spacing={3}>
         <Grid size={{ xs: 12, sm: 12, lg: 3, xl: 3 }}>
            <Card sx={{ p: { lg: 3, md: 3, xs: 2 }, textAlign: 'center' }}>
               <UploadLogoAdmin />
            </Card>
         </Grid>
         <Grid size={{ xs: 12, sm: 12, lg: 9, xl: 9 }}>
            <Card
               sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  p: { lg: 3, md: 3, xs: 2 },
               }}
            >
               <FormikProvider value={formik}>
                  <Form>
                     <Box
                        sx={{
                           mb: 5,
                           ml: 2,
                           textAlign: 'center',
                        }}
                     />

                     <Stack direction="column" sx={{ px: { lg: 3, md: 3, xs: 1 } }} spacing={{ md: 3, xs: 2 }}>
                        <Stack direction={{ md: 'row', xs: 'column' }} spacing={{ md: 3, xs: 2 }}>
                           <InputField
                              disabled={!!companyData?.taxID}
                              name="taxID"
                              slotProps={{
                                 input: {
                                    endAdornment: !companyData?.taxID && (
                                       <Button
                                          onClick={handleOnSearch}
                                          color="primary"
                                          variant="contained"
                                          sx={{ height: 40, ml: 1.5 }}
                                          disabled={isCompanyDataFetching || !values.taxID}
                                       >
                                          <Box component={Search} sx={{ color: 'white', width: 20, height: 20 }} />
                                       </Button>
                                    ),
                                 },
                              }}
                              onChange={(e) => setFieldValue('taxID', e.target.value.replace(' ', ''))}
                              label={t('TRANSLATION:formFields.taxID')}
                           />
                           <TextField
                              disabled
                              fullWidth
                              value={
                                 data && data[values.address.country]
                                    ? data[values.address.country].name
                                    : values.address.country
                              }
                              label={t('TRANSLATION:formFields.country')}
                           />
                        </Stack>
                        <Stack direction={{ md: 'row', xs: 'column' }} alignItems="center" spacing={{ md: 3, xs: 2 }}>
                           <InputField
                              label={t('TRANSLATION:formFields.companyName')}
                              name="companyName"
                              inputRef={textFieldRef}
                              disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN)}
                              slotProps={{
                                 input: {
                                    sx: {
                                       fontSize: 20,
                                       fontWeight: 'bold',
                                    },
                                 },
                              }}
                           />
                        </Stack>
                        <Stack direction={{ md: 'row', xs: 'column' }} spacing={{ md: 3, xs: 2 }}>
                           {values?.address.country === 'Poland' ? (
                              <SelectField
                                 searcher
                                 noMinWidth
                                 fullWidth
                                 label={t('TRANSLATION:formFields.voivodeship')}
                                 inputDisabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                                 inputVariant="outlined"
                                 name="address.voivodeship"
                                 options={options.voivodeshipOptions}
                              />
                           ) : (
                              <InputField
                                 label={t('TRANSLATION:formFields.voivodeship')}
                                 fullWidth
                                 name="address.voivodeship"
                                 disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                              />
                           )}
                           {values?.address.country === 'Poland' ? (
                              <SelectField
                                 searcher
                                 label={t('TRANSLATION:formFields.county')}
                                 inputVariant="outlined"
                                 fullWidth
                                 noMinWidth
                                 name="address.county"
                                 inputDisabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                                 options={options.countyOptions}
                              />
                           ) : (
                              <InputField
                                 fullWidth
                                 label={t('TRANSLATION:formFields.county')}
                                 name="address.county"
                                 disabled={
                                    !formik.values?.address.voivodeship ||
                                    !user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) ||
                                    isLoading
                                 }
                              />
                           )}
                           <InputField name="address.city" label={t('TRANSLATION:formFields.city')} />
                        </Stack>
                        <Stack direction={{ md: 'row', xs: 'column' }} spacing={{ md: 3, xs: 2 }}>
                           <InputField
                              name="address.zipCode"
                              label={t('TRANSLATION:formFields.postCode')}
                              disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                           />
                           <InputField
                              label={t('TRANSLATION:formFields.street')}
                              name="address.street"
                              disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                           />
                           <InputField
                              label={t('TRANSLATION:formFields.houseNumber')}
                              name="address.houseNumber"
                              disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                           />
                        </Stack>
                        <Stack direction={{ md: 'row', xs: 'column' }} spacing={{ md: 3, xs: 2 }}>
                           <InputField
                              type="number"
                              label={t('TRANSLATION:formFields.workshopEmployees')}
                              slotProps={{
                                 htmlInput: {
                                    min: 0,
                                 },
                              }}
                              disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                              name="typeService.workshopPositions"
                              required
                           />
                           <InputField
                              type="number"
                              label={t('TRANSLATION:formFields.shopEmployees')}
                              slotProps={{
                                 htmlInput: {
                                    min: 0,
                                 },
                              }}
                              disabled={!user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) || isLoading}
                              name="typeService.shopPositions"
                              required
                           />
                        </Stack>
                     </Stack>
                     {user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                           <ContainedButton disabled={isLoading} type="submit" variant="contained">
                              {t('TRANSLATION:save')}
                           </ContainedButton>
                        </Box>
                     )}
                  </Form>
               </FormikProvider>
            </Card>
         </Grid>
      </Grid>
   )
}
